import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  DollarSign, 
  TrendingUp, 
  Calendar,
  Loader2,
  Info,
  ArrowUpRight,
  Lock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * EarningsSummary - Displays user earnings (minimal, period totals only)
 * 
 * Per spec:
 * - Users see: eligibility status, period totals
 * - Users do NOT see: CPM, CPC, impressions, clicks, per-ad revenue
 * - Neutral copy: "Support generated by your space"
 */
const EarningsSummary = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (token) {
      fetchEarnings();
    } else {
      setLoading(false);
    }
  }, [token]);
  
  const fetchEarnings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/earnings/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEarnings(data);
      } else {
        const error = await res.json();
        setEarnings({ eligible: false, reason: error.detail });
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
      toast.error("Failed to load earnings data");
    } finally {
      setLoading(false);
    }
  };
  
  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Please sign in to view your earnings.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // Not eligible - show upgrade prompt
  if (!earnings?.eligible) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-muted-foreground" />
              Revenue Sharing
            </CardTitle>
            <CardDescription>
              Revenue sharing from supported placements on your events
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="mb-4">
              <Info className="h-4 w-4" />
              <AlertDescription>
                {earnings?.reason || "Revenue sharing is available to Local Featured and Market Sponsor business tiers."}
              </AlertDescription>
            </Alert>
            
            <div className="space-y-4">
              <h3 className="font-medium">How it works:</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="font-medium text-foreground">1.</span>
                  Move to a Local Featured or Market Sponsor business plan
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-foreground">2.</span>
                  Host events and activities through your business profile
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-foreground">3.</span>
                  Support generated by your space accrues to your account
                </li>
              </ul>
              
              <Button onClick={() => navigate('/subscription')} className="mt-4">
                <ArrowUpRight className="h-4 w-4 mr-2" />
                View Business Plans
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Calculate progress to threshold
  const thresholdProgress = Math.min(100, (earnings.all_time_total / earnings.minimum_threshold) * 100);
  
  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Earnings</h1>
        <p className="text-muted-foreground">
          {earnings.message}
        </p>
      </div>
      
      {/* Current Period Card */}
      <div className="grid gap-4 md:grid-cols-2 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              This Month ({earnings.current_period})
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              ${earnings.period_total.toFixed(2)}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <TrendingUp className="h-4 w-4" />
              All Time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              ${earnings.all_time_total.toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Threshold Progress */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Payout Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>${earnings.all_time_total.toFixed(2)} accrued</span>
              <span>${earnings.minimum_threshold.toFixed(2)} minimum</span>
            </div>
            <Progress value={thresholdProgress} className="h-2" />
            <p className="text-xs text-muted-foreground mt-2">
              {earnings.threshold_met 
                ? "You've reached the minimum threshold. Payouts will be available in a future update."
                : `$${(earnings.minimum_threshold - earnings.all_time_total).toFixed(2)} more to reach the minimum payout threshold.`
              }
            </p>
          </div>
        </CardContent>
      </Card>
      
      {/* Info Section */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Earnings accrue from sponsored placements on your events. 
          Higher participation in your events increases your share.
          Payouts are not yet available but your balance is being tracked.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default EarningsSummary;
